import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import { logError } from '../../Helpers/errorLogger';

/**
 * Error boundary that wraps the AG Grid to prevent grid calculation errors
 * (e.g. bad valueGetters, null column data) from crashing the entire page.
 *
 * Renders a recoverable error UI with a "Reload Grid" button that resets
 * the boundary state, allowing the parent to remount the grid.
 */
class AgGridErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    logError(error, { source: 'AgGridErrorBoundary', componentStack: info?.componentStack });
    if (process.env.NODE_ENV !== 'production') {
      console.error('[AgGridErrorBoundary] Grid crashed:', error, info);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            alignItems: 'center',
            border: '1px solid',
            borderColor: 'warning.light',
            borderRadius: 2,
            bgcolor: 'warning.50',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            justifyContent: 'center',
            minHeight: 300,
            p: 4,
            textAlign: 'center',
          }}
        >
          <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 48 }} />
          <Typography variant="h6" color="warning.dark">
            The grid encountered an error and could not render.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This is usually caused by a change with incomplete data. Your data has been saved —
            reload the grid to continue.
          </Typography>
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <Typography
              variant="caption"
              color="error"
              sx={{
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
                maxWidth: 600,
                p: 1,
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {this.state.error.toString()}
            </Typography>
          )}
          <Button
            onClick={this.handleReset}
            startIcon={<RefreshIcon />}
            variant="contained"
            color="warning"
          >
            Reload Grid
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default AgGridErrorBoundary;
